/* eslint-disable max-len */

/**
 * Sorts an array of column objects alphabetically by a given key, in place.
 * @param {Array} cols - Array of column objects to sort
 * @param {string} sortKey - The key on each object to sort by
 * @returns {Array} The sorted array (mutated in place)
 */
const alphabetizeCols = (cols, sortKey) => {
  cols.sort((a, b) => {
    const keyA = a[sortKey];
    const keyB = b[sortKey];

    if (keyA < keyB) {
      return -1;
    } if (keyA > keyB) {
      return 1;
    }
    return 0;
  });

  return cols;
};

/**
 * Safely converts a value to its string representation.
 * Returns an empty string if the value is falsy, avoiding toString() errors on undefined/null.
 * @param {*} obj - The value to convert
 * @returns {string} The string representation, or empty string if falsy
 */
function boolToString(obj) {
  // can't just do toString() b/c if that kv dne, we'll get an error
  if (obj) {
    return obj.toString();
  }
  return '';
}

/**
 * Structure the client data from the API to be used in the data grid table.
 * Sort Favorite clients into a separate array to be pinned to the grid.
 * @param {Array} data - The data received from the database of all clients
 * @param {Array} favorites - The list of favorite clients
 * @returns
 */
function createClientRows(data, favorites) {
  const formedRows = [];
  const pinnedRows = [];
  data[0].forEach((item) => {
    const current = item;
    // If the client is a favorite, add it to the pinnedRows array, otherwise add it to the formedRows array
    (favorites.includes(current.client_id) ? pinnedRows : formedRows).push({
      id: current.client_id,
      code: current.client_id,
      jiraname: current.jira_project_name,
      fullname: current.client_full_name,
      clienttype: current.client_type,
      adaimember: boolToString(current.is_advantage_member),
      adsconfiguration: current.ads_configuration,
      actblueids: current.actblue_entity_ids,
      actionsdatasource: current.action_platform,
      adaisuppresseddomains: current.suppressed_domains,
      bingids: current.bing_account_ids,
      campaignmonitorlocation: current.campaign_monitor_location,
      classyorgid: current.classy_org_id,
      codedicurl: current.code_dictionary_url,
      curateautoupload: boolToString(current.curate_delivery_auto_upload),
      curatedays: current.curate_days,
      curatefrequency: current.curate_frequency,
      curatesubscriber: boolToString(current.curate_subscriber),
      emailconfigurations: current.email_configurations,
      emailexhaustdatasource: current.exhaust_platform,
      emailvalidationprovider: current.email_validation_provider,
      excludematchqualifier: current.exclude_from_match_qualifier,
      facebookids: current.facebook_account_ids,
      facebookloc: current.facebook_locations,
      featurize: boolToString(current.featurize),
      fiscalyearstart: current.fiscal_year_start,
      googleids: current.google_account_ids,
      goopid: current.goop_client_ids,
      hubspotstitchintegrationname: current.hubspot_stitch_integration_name,
      hubsrcpath: current.hub_source_path,
      inboxmonsterrule: current.inbox_monster_rule,
      isactive: boolToString(current.is_active),
      isadsreportingactive: boolToString(current.is_ads_reporting_active),
      identity: current.identity,
      islowinventory: boolToString(current.is_low_inventory),
      ismidlevelmember: boolToString(current.is_mid_level_member),
      isstrategypartner: boolToString(current.is_strategy_partner),
      mailchimpdir: current.mailchimp_directory,
      mmclients: current.multi_member_clients,
      mmrequired: boolToString(current.multi_member_required),
      mmmlrequired: boolToString(current.multi_member_mid_level_required),
      mmsmsclients: current.multi_member_sms_clients,
      mmmlclients: current.multi_member_mid_level_clients,
      ngpactbluemanualcodes: current.ngp_actblue_manual_codes,
      ngpeadb: current.ngp_everyaction_database_name,
      ngpeaserver: current.ngp_everyaction_server_name,
      notes: current.notes,
      numeroaccountids: current.numero_account_ids,
      outbrainids: current.outbrain_account_ids,
      pardotloc: current.pardot_location,
      pausedata: current.pause_data,
      peopledatasource: current.people_platform,
      salesforceloc: current.salesforce_location,
      salsakey: current.salsa_organization_key,
      sector: current.sector,
      sfgid: current.sfg_surveys_client_ids,
      sharecapmaxshares: current.share_cap_max_shares,
      sharecapmonths: current.share_cap_months,
      slackchannel: current.annelewis_slack_channel,
      smsconfigurations: current.sms_configurations,
      smsdatasource: current.sms_platform,
      stwaccountids: current.stw_subaccount_ids,
      stwnoncoopsubaccountids: current.stw_non_coop_subaccount_ids,
      switchboarddataset: current.switchboard_dataset_name,
      tatangoid: current.tatango_client_id,
      timezone: current.timezone,
      totemid: current.totem_client_ids,
      transactionsdatasource: current.transaction_platform,
      transactionsofflinedatasource: current.transactions_offline_platform,
      twitterids: current.twitter_account_ids,
      vendor: current.vendor,
      updatedat: current.updatedAt,
      createdat: current.createdAt,
    });
  });
  return { formedRows, pinnedRows };
}

/**
 * Formats code data from the API for use in the data grid table.
 * @param {Array} data - Array where data[0] contains the raw code items from the database
 * @returns {Array} The formatted rows ready for the data grid table
 */
const createCodeRows = (data) => {
  // eslint-disable-next-line array-callback-return
  const myrows = data[0].map((item) => {
    const current = item;
    return {
      id: current.id,
      client_id: current.client_id,
      code: current.code,
      source: current.source,
      channel: current.channel,
      channel_category: current.channel_category,
      send_size: current.send_size,
      send_cost: current.send_cost,
      acquisitions: current.acquisitions,
      audience: current.audience,
      audience_category: current.audience_category,
      revenue: current.revenue,
      revenue_category: current.revenue_category,
      type: current.type,
      fixed_cost_cpa_override: current.fixed_cost_cpa_override,
      fixed_cost_media_spend: current.fixed_cost_media_spend,
      most_recent_spend: current.most_recent_spend,
      most_recent_spend_date: current.most_recent_spend_date,
      platforms_with_spend: current.platforms_with_spend,
      total_spend: current.total_spend,
      transactions: current.transactions,
      most_recent_transaction_date: current.most_recent_transaction_date,
      from_goop: current.from_goop,
      createdAt: current.createdAt,
      page: current.page,
      actblue_managing_entities: current.actblue_managing_entities,
      version: current.version,
      jira_ticket_num: current.jira_ticket_num,
    };
  });
  return myrows;
};

/**
 * Formats source data from the API for use in the data grid table.
 * @param {Array} data - Array where data[0] contains the raw source items from the database
 * @returns {Array} The formatted rows ready for the data grid table
 */
const createSourceRows = (data) => {
  // eslint-disable-next-line array-callback-return
  const myrows = data[0].map((item) => {
    const current = item;
    return {
      channel_category: current.channel_category,
      source: current.source,
      client_id: current.client_id,
      id: current.id,
      from_goop: current.from_goop,
      send_date: current.send_date,
      createdAt: current.createdAt,
    };
  });
  return myrows;
};

/**
   * createTransactionRows - format data from api to use in data grid table
   * @param {Object} rowData The data received from the database
   * @returns {Array} The formatted rows ready for data grid table
   */
const createTransactionRows = (data) => {
  // eslint-disable-next-line array-callback-return
  const myrows = data[0].map((item) => {
    const current = item;
    return {
      amount: current.amount,
      client_id: current.client_id,
      code: current.code,
      code_source: current.code_source.items[0] ? current.code_source.items[0].source : null,
      createdAt: current.createdAt,
      email: current.email,
      exclude_flag: current.exclude_flag,
      exclusion_updated_dt: current.exclusion_updated_dt,
      foreign_client_key: current.foreign_client_key,
      foreign_platform: current.foreign_platform,
      foreign_transaction_key: current.foreign_transaction_key,
      id: current.id,
      mobile_flag: current.mobile_flag,
      recurrence: current.recurrence,
      recurring_period: current.recurring_period,
      recurring_type: current.recurring_type,
      refund_flag: current.refund_flag,
      transaction_dt: current.transaction_dt,
      updatedAt: current.updatedAt,
    };
  });
  return myrows;
};

/**
   * createInsightsRows - format data from api to use in data grid table
   * @param {Object} rowData The data received from the database
   * @param {Boolean} isCohort If the table is a cohort table (it has extra columns)
   * @param {String} tableName optional table name to handle special column for reac cohort table
   * @returns {Array} The formatted rows ready for data grid table
   */
const createInsightsRows = (data, isCohort, tableName) => {
  const myrows = data[0].map((item) => {
    const current = item;
    const formatted = {
      id: current.id,
      Break_even_date: current.Break_even_date,
      Days_to_break_even: current.Days_to_break_even,
      Projected_break_even_date: current.Projected_break_even_date,
      Projected_days_to_break_even: current.Projected_days_to_break_even,
      ROAS: current.ROAS,
      ROAS_Over_Days: current.ROAS_Over_Days,
      Unsubs: current.Unsubs,
      average_gift: current.average_gift,
      category: current.category,
      click_emails: current.click_emails,
      click_rate: current.click_rate,
      clicks: current.clicks,
      client: current.client,
      client_type: current.client_type,
      contributions: current.contributions,
      contributions_over_sends: current.contributions_over_sends,
      donors: current.donors,
      emails_acquired: current.emails_acquired,
      emails_messaged: current.emails_messaged,
      first_delivery_date: current.first_delivery_date, // aws date time
      first_uploaded_date: current.first_uploaded_date,
      gifts_over_opens: current.gifts_over_opens,
      hard_bounce_emails: current.hard_bounce_emails,
      hard_bounce_rate: current.hard_bounce_rate,
      hard_bounces: current.hard_bounces,
      jira_ticket_num: current.jira_ticket_num,
      open_emails: current.open_emails,
      open_rate: current.open_rate,
      opens: current.opens,
      optout_emails: current.optout_emails,
      optout_rate: current.optout_rate,
      optouts: current.optouts,
      pixel_load_emails: current.pixel_load_emails,
      pixel_load_rate: current.pixel_load_rate,
      pixel_loads: current.pixel_loads,
      revenue: current.revenue,
      sends: current.sends,
      soft_bounce_emails: current.soft_bounce_emails,
      soft_bounce_rate: current.soft_bounce_rate,
      soft_bounces: current.soft_bounces,
      spam_emails: current.spam_emails,
      spam_rate: current.spam_rate,
      spams: current.spams,
      spend: current.spend,
      delivery_month: current.delivery_month,
      delivery_year: current.delivery_year,
      send_emails_past_30: current.send_emails_past_30,
      cost_per_donor: current.cost_per_donor,
      updated_dt: current.updated_dt,
    };

    if (isCohort) {
      formatted.cohort = current.cohort;
      formatted.cohort_name = current.cohort_name;
    }

    if (tableName === 'Reac Cohort') {
      formatted.recurring_flag = current.recurring_flag;
    }

    return formatted;
  });
  return myrows;
};

/**
 * Safely parses the upload_results field from the database, which may be a JSON string or already an object.
 * @param {string|Object} raw - The raw upload_results value from the API
 * @returns {Object|null} The parsed object, or null if parsing fails
 */
const parseUploadResults = (raw) => {
  if (typeof raw === 'object' && raw !== null) return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * Formats an updated/total pair as a "updated/total" string for display in the import history table.
 * @param {number|null} updated - The number of records successfully updated
 * @param {number|null} total - The total number of records attempted
 * @returns {string} Formatted count string, or empty string if either value is null
 */
const formatCount = (updated, total) => {
  if (updated == null || total == null) return '';
  return `${updated}/${total}`;
};

/**
 * Extracts the exclusion and inclusion count summary for exclusions-type import history rows.
 * @param {Object|null} uploadResults - The parsed upload_results object
 * @returns {{ exclusionCount: string, inclusionCount: string, totalUpdated: number, totalAll: number }} Formatted counts
 */
const getExclusionsCountSummary = (uploadResults) => {
  const updatedExclusion = uploadResults ? uploadResults.exclusion_count : null;
  const updatedInclusion = uploadResults ? uploadResults.inclusion_count : null;
  const totalRequests = uploadResults && uploadResults.total_requests != null ? uploadResults.total_requests : null;
  const totalUpdated = uploadResults && uploadResults.total_updated != null
    ? uploadResults.total_updated
    : (updatedExclusion || 0) + (updatedInclusion || 0);

  let totalExclusion = null;
  let totalInclusion = null;
  if (totalRequests !== null) {
    if ((updatedExclusion || 0) > 0 && (updatedInclusion || 0) === 0) {
      totalExclusion = totalRequests;
      totalInclusion = 0;
    } else if ((updatedInclusion || 0) > 0 && (updatedExclusion || 0) === 0) {
      totalExclusion = 0;
      totalInclusion = totalRequests;
    }
  }

  const totalAll = totalRequests !== null ? totalRequests : (totalExclusion || 0) + (totalInclusion || 0);

  return {
    exclusionCount: formatCount(updatedExclusion, totalExclusion),
    inclusionCount: formatCount(updatedInclusion, totalInclusion),
    totalUpdated,
    totalAll,
  };
};

/**
 * Formats import history data from the API for use in the data grid table.
 * For p2p_bulk_upload rows, status and counts are derived from upload_results JSON;
 * the error field stores the signed_url string for downloading the error report.
 * For exclusions-type rows, counts are derived from the legacy top-level count fields or upload_results.
 * @param {Array} data - Array where data[0] contains raw ImportHistory items from the API
 * @returns {Array} The formatted rows ready for the data grid table
 */
const createImportHistoryRows = (data) => {
  const myrows = data[0].map((item) => {
    const ur = parseUploadResults(item.upload_results);
    const warning = item.warning && item.warning !== 'null' ? item.warning : '';

    let status;
    let error = '';
    let exclusionCount = '';
    let inclusionCount = '';
    let count = '';
    let signedUrl = null;

    if (item.upload_type === 'p2p_bulk_upload') {
      // error field holds the signed_url string for p2p_bulk_upload rows
      const hasError = item.error && item.error !== 'null';

      signedUrl = hasError ? item.error : null;
      if (warning) status = 'Partial';
      else if (hasError) status = 'Failed';
      else status = 'Success';
      inclusionCount = formatCount(ur && ur.total_updated, ur && ur.total_request);
      count = inclusionCount;
    } else {
      const countSummary = getExclusionsCountSummary(ur);
      exclusionCount = countSummary.exclusionCount;
      inclusionCount = countSummary.inclusionCount;
      const { totalUpdated, totalAll } = countSummary;
      const someNotUpdated = typeof totalUpdated === 'number' && typeof totalAll === 'number' && totalUpdated < totalAll;
      status = someNotUpdated ? 'Failed' : 'Success';
      error = status === 'Failed' && item.error && item.error !== 'null' ? item.error : '';
    }

    return {
      id: item.s3_key,
      client: item.client,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      error,
      warning,
      status,
      exclusionCount,
      inclusionCount,
      count,
      timestamp: item.timestamp,
      csv_name: item.csv_name || item.s3_key || 'Unknown',
      signed_url: signedUrl,
    };
  });
  return myrows;
};

const createSumBySourceExportResultsRows = (data) => {
  /**
   * @param {Array} data - data from graphql query
   * @returns {Array} The formatted rows ready for data grid table
   */
  const myrows = data[0].map((item) => {
    const current = item;
    let reportType = '';
    const parsedConfigs = JSON.parse(current.run_config);
    const args = parsedConfigs.arguments;
    if (args.aggType === 'sumBySource') {
      reportType = 'Sum by Source';
    } else {
      reportType = 'Sum by Channel Category';
    }
    const breakdownPeriod = args.breakdownPeriod[0].toUpperCase() + args.breakdownPeriod.slice(1);
    const dateRange = `${args.startDate} to ${args.endDate}`;
    return {
      id: current.id,
      status: current.status,
      signed_s3_url: current.signed_s3_url,
      errors: current.errors,
      saved_dt: current.saved_dt,
      report_type: reportType,
      breakdown_period: breakdownPeriod,
      date_range: dateRange,
    };
  });
  return myrows;
};

const createInsightsNonCohortRows = (data) => createInsightsRows(data, false, null);

const createInsightsCohortRows = (data) => createInsightsRows(data, true, null);

const createInsightsReacCohortRows = (data) => createInsightsRows(data, true, 'Reac Cohort');

export {
  alphabetizeCols,
  createClientRows,
  createCodeRows,
  createSourceRows,
  createTransactionRows,
  createInsightsNonCohortRows,
  createInsightsCohortRows,
  createInsightsReacCohortRows,
  createImportHistoryRows,
  createSumBySourceExportResultsRows,
};
